import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCheck } from 'lucide-react';
import { Button, DataTable, DateField, ErrorState, FormField, PageHeader, Panel, StatusBadge, useConfirm, useToast, type Column } from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { ApiError, errorMessage } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useBulkApprovalList, useBulkApprove, type BulkApprovalRow } from './api';
import './vessels.css';

/** S-12 参加船名簿 一括承認（基金のみ） */
export function BulkApprovalPage() {
  const navigate = useNavigate();
  const { year, yearPath } = useFiscalYear();
  const toast = useToast();
  const confirm = useConfirm();
  const { data, isPending, error, refetch } = useBulkApprovalList(year);
  const approve = useBulkApprove();
  // 承認期間の既定は 年度の 4/1〜翌年 3/31
  const [from, setFrom] = useState(`${year}-04-01`);
  const [to, setTo] = useState(`${year + 1}-03-31`);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [periodError, setPeriodError] = useState<string>();

  const columns: Column<BulkApprovalRow>[] = useMemo(
    () => [
      { key: 'businessOperatorId', header: 'ID', numeric: true, width: 80 },
      { key: 'name', header: '事業実施者', sortable: true },
      { key: 'vesselCount', header: '参加船数', numeric: true, width: 100 },
      {
        key: 'approvedCount',
        header: '承認済み',
        numeric: true,
        width: 110,
        render: (r) => (r.vesselCount > 0 && r.approvedCount === r.vesselCount ? <StatusBadge tone="success" dot>{r.approvedCount} 隻</StatusBadge> : `${r.approvedCount} 隻`),
      },
      {
        key: 'bulkApprovalFrom',
        header: '現在の一括承認期間',
        width: 260,
        render: (r) => (r.bulkApprovalFrom ? <span className="bs-num">{formatDate(r.bulkApprovalFrom)} 〜 {formatDate(r.bulkApprovalTo)}</span> : <span style={{ color: 'var(--text-muted)' }}>未設定</span>),
      },
    ],
    [],
  );

  const onApprove = async () => {
    setPeriodError(undefined);
    if (!from || !to || from > to) {
      setPeriodError('承認期間に誤りがあります。');
      return;
    }
    if (selected.length === 0) {
      toast.error('更新できませんでした', '事業実施者を1件以上選択してください。');
      return;
    }
    const ok = await confirm({
      title: '一括承認を実行しますか？',
      message: `選択した ${selected.length} 件の事業実施者の全参加船について、承認期間を ${formatDate(from)} 〜 ${formatDate(to)} に設定します。\n船長・乗組員・乗船人数の承認期間も同じ期間で上書きされます。`,
      confirmLabel: '承認する',
    });
    if (!ok) return;
    try {
      const r = await approve.mutateAsync({ fiscalYear: year, businessOperatorIds: selected.map(Number), approvalFrom: from, approvalTo: to });
      toast.success('一括承認を実行しました', `${r.updatedVessels} 隻の承認期間を更新しました`);
      setSelected([]);
    } catch (e) {
      if (e instanceof ApiError && e.fieldErrors.approvalTo) setPeriodError(e.fieldErrors.approvalTo);
      else toast.error('更新できませんでした', errorMessage(e));
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="船舶・計画 ／ 参加船名簿"
        title="参加船名簿 一括承認"
        description="事業実施者ごとに、全参加船の承認期間（参加期間）を一括で設定します。基金のみ操作できます。"
        actions={<Button icon={<ArrowLeft size={16} />} onClick={() => navigate(yearPath('vessels'))}>名簿一覧へ戻る</Button>}
      />
      <Panel title="承認期間" description="既定は事業年度の 4/1 〜 翌年 3/31 です。">
        <div className="bs-bulk-period">
          <FormField label="承認期間 FROM" required error={periodError}>
            <DateField showWareki value={from} onChange={(e) => setFrom(e.target.value)} invalid={!!periodError} />
          </FormField>
          <FormField label="承認期間 TO" required>
            <DateField showWareki value={to} onChange={(e) => setTo(e.target.value)} invalid={!!periodError} />
          </FormField>
        </div>
      </Panel>
      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          caption="事業実施者一覧"
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(r) => r.businessOperatorId}
          loading={isPending}
          selectable
          selectedKeys={selected}
          onSelectionChange={setSelected}
          dense
          toolbar={
            <Button variant="primary" icon={<CheckCheck size={16} />} onClick={onApprove} loading={approve.isPending} disabled={selected.length === 0}>
              選択した事業実施者を一括承認（{selected.length}）
            </Button>
          }
        />
      )}
    </>
  );
}
